import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Constants
const DEFAULT_PAGE_SIZE = 10;
const FILTER_PARAMS = ['appointmentType', 'status', 'mode', 'practitionerName', 'memberName', 'riskStatus', 'name'];

// Utility function to create auth config
const createAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json"
  }
});

// Utility function to handle error responses
const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.response_message ||
                      error.response?.data?.message ||
                      defaultMessage;
  return {
    status: "error",
    message: errorMessage
  };
};

// Utility function to build filter parameters
const buildFilterParams = (filters, page = 0, size = DEFAULT_PAGE_SIZE) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });

  // Handle date filters
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  // Handle array-based filters
  FILTER_PARAMS.forEach(filterKey => {
    if (filters[filterKey] !== undefined && filters[filterKey] !== null) {
      if (filters[filterKey].includes(',')) {
        filters[filterKey].split(',').forEach(value =>
          params.append(filterKey, value.trim())
        );
      } else {
        params.append(filterKey, filters[filterKey]);
      }
    }
  });

  return params;
};

// Fetch appointment statistics
export const getAppointmentStats = createAsyncThunk(
  "appointments/getAppointmentStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/appointment-stats`,
        createAuthConfig()
      );
      return {
        status: "success",
        data: response.data
      };
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch appointment statistics"));
    }
  }
);

// Fetch appointments list with filters and pagination
export const getAppointmentsList = createAsyncThunk(
  "appointments/getAppointmentsList",
  async ({ page = 0, size = DEFAULT_PAGE_SIZE, filters = {} }, { rejectWithValue }) => {
    try {
      const params = buildFilterParams(filters, page, size);

      const response = await axios.get(
        `${API_BASE_URL}/admin/appointments-list?${params.toString()}`,
        createAuthConfig()
      );

      return {
        status: "success",
        data: response.data,
        requestParams: { page, size, filters }
      };
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch appointments list"));
    }
  }
);

// Fetch appointments trend data (using delivery-trend-new endpoint)
export const getAppointmentsTrend = createAsyncThunk(
  "appointments/getAppointmentsTrend",
  async (timeFilter, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/summary/delivery-trend-new`,
        timeFilter,
        createAuthConfig()
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch appointments trend"));
    }
  }
);

const appointmentsSlice = createSlice({
  name: "appointments",
  initialState: {
    // Statistics data
    stats: {
      data: null,
      loading: false,
      error: null
    },
    // Appointments list data
    list: {
      data: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      loading: false,
      error: null
    },
    // Appointments trend data
    trend: {
      data: [],
      loading: false,
      error: null
    },
    // Current filters applied
    filters: {}
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setPageSize: (state, action) => {
      state.list.pageSize = action.payload;
      state.list.currentPage = 0; // Reset to first page
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Appointment Stats
      .addCase(getAppointmentStats.pending, (state) => {
        state.stats.loading = true;
        state.stats.error = null;
      })
      .addCase(getAppointmentStats.fulfilled, (state, action) => {
        state.stats.loading = false;
        if (action.payload.status === "success") {
          state.stats.data = action.payload.data.payload;
        } else {
          state.stats.error = action.payload.message;
        }
      })
      .addCase(getAppointmentStats.rejected, (state, action) => {
        state.stats.loading = false;
        state.stats.error = action.payload?.message || "Failed to fetch appointment statistics";
      })
      
      // Get Appointments List
      .addCase(getAppointmentsList.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(getAppointmentsList.fulfilled, (state, action) => {
        state.list.loading = false;
        if (action.payload.status === "success") {
          const responseData = action.payload.data;
          const requestParams = action.payload.requestParams;
          
          state.list.data = responseData.content || [];
          state.list.totalElements = responseData.totalElements || 0;
          state.list.totalPages = responseData.totalPages || 0;
          state.list.currentPage = responseData.number || 0;
          
          // Update pagination state from request params
          if (requestParams) {
            state.list.pageSize = requestParams.size || state.list.pageSize;
          }
        } else {
          state.list.error = action.payload.message;
        }
      })
      .addCase(getAppointmentsList.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload?.message || "Failed to fetch appointments list";
      })

      // Get Appointments Trend
      .addCase(getAppointmentsTrend.pending, (state) => {
        state.trend.loading = true;
        state.trend.error = null;
      })
      .addCase(getAppointmentsTrend.fulfilled, (state, action) => {
        state.trend.loading = false;
        state.trend.data = action.payload.data?.payload || [];
      })
      .addCase(getAppointmentsTrend.rejected, (state, action) => {
        state.trend.loading = false;
        state.trend.error = action.error.message;
      });
  },
});

export const { setFilters, clearFilters, setPageSize } = appointmentsSlice.actions;
export default appointmentsSlice.reducer;